import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { xcss, Box, Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { INLINE_CREATE_PARAM } from '@atlassian/jira-business-constants/src/index.tsx';
import { useFilteredViewContextInput } from '@atlassian/jira-business-filters/src/controllers/default-query-input/index.tsx';
import { useSimpleSearchJQL } from '@atlassian/jira-business-filters/src/controllers/simple-search/index.tsx';
import { ThemedFilterEmptyState } from '@atlassian/jira-business-filters/src/ui/themed-filter-empty-state/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';
import { useJWMListFeatures } from '../../../controllers/features-context/index.tsx';
import Illustration from './illustration/index.tsx';
import messages from './messages.tsx';

export const ListEmptyState = () => {
	const { formatMessage } = useIntl();
	const [, setInlineCreateParam] = useQueryParam(INLINE_CREATE_PARAM);
	const handleClick = useCallback(() => setInlineCreateParam('true'), [setInlineCreateParam]);

	const { isSoftwareFieldsEnabled } = useJWMListFeatures();

	let createButtonText = messages.createItemButton;
	if (isSoftwareFieldsEnabled) {
		createButtonText = expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
			? messages.createIssueButtonIssueTermRefresh
			: messages.createIssueButton;
	}

	return (
		<Wrapper>
			<Illustration />
			<HeadingWrapper>{formatMessage(messages.emptyStateHeading)}</HeadingWrapper>
			<Box xcss={contentWrapperStyles}>{formatMessage(messages.emptyStateDescription)}</Box>
			<ThemedButton appearance="primary" onClick={handleClick}>
				{formatMessage(createButtonText)}
			</ThemedButton>
		</Wrapper>
	);
};

export const EmptyState = ({ reason }: { reason?: 'INVALID_JQL' }) => {
	const { isFiltering, jqlErrors } = useFilteredViewContextInput({ view: 'list' });
	const { isSearching } = useSimpleSearchJQL();

	let emptyStateMessage = <ListEmptyState />;

	if (reason === 'INVALID_JQL' && fg('list-handle-field-missing-handler-server-error')) {
		emptyStateMessage = <ThemedFilterEmptyState reason="INVALID_JQL" />;
	} else if (isSearching || isFiltering) {
		emptyStateMessage = (
			<ThemedFilterEmptyState reason={jqlErrors.length ? 'INVALID_JQL' : 'NO_RESULTS'} />
		);
	}

	return <Flex xcss={wrapperStyles}>{emptyStateMessage}</Flex>;
};

const Wrapper = ({ children }: { children: React.ReactNode }) => (
	<Box xcss={emptyWrapperStyles}>{children}</Box>
);

const emptyWrapperStyles = xcss({
	textAlign: 'center',
	margin: 'auto',
	width: '400px',
	padding: 'space.300',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	background: Tokens.ELEVATION_SURFACE_CONTRAST_BOLDER,
	borderRadius: 'border.radius.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.h2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT,
	marginTop: token('space.100', '8px'),
});

const contentWrapperStyles = xcss({
	marginTop: 'space.150',
	marginBottom: 'space.300',
});

const wrapperStyles = xcss({
	marginTop: 'space.1000',
	paddingTop: 'space.800',
	width: '100%',
});
